import { useMemo, useState } from "react";
import type { DemoAttendeeRow } from "../sql";
import { uniqueDemos } from "../helpers";
import type { ViewMode } from "../helpers";
import { HBarChart } from "../../../lib/funnel/charts";
import { WordCloud } from "../../../components/WordCloud";
import { PRIMARY, MUTED, FG1 } from "../../../lib/funnel/theme";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

type MentionProps = {
  rows: DemoAttendeeRow[];
  viewMode: ViewMode;
  selectionLabel?: string | null;
  onClearSelection?: () => void;
  pagePills?: FilterPillSpec[];
};

function pillsFor(p: MentionProps): FilterPillSpec[] {
  const out: FilterPillSpec[] = [...(p.pagePills ?? [])];
  if (p.selectionLabel) {
    out.unshift({
      kind: "selection",
      label: `Selected: ${p.selectionLabel}`,
      onClear: p.onClearSelection,
    });
  }
  return out;
}

// Bar charts off the follow_up_context jsonb arrays. These panels are
// demo-level by design (the array is per demo, not per attendee), so they
// always count distinct demo_external_id regardless of the global toggle.

// Recursively flatten any nested array / JSON-encoded-array-string structure
// down to a flat list of leaf strings. Without this, jsonb fields that arrive
// as `'["a","b"]'` strings — or arrays containing such strings (which happens
// after company-mode rollup unions) — render as a single bracketed line per
// demo instead of one item per phrase.
function extractStrings(v: unknown): string[] {
  if (v == null || v === "") return [];
  if (Array.isArray(v)) {
    return v.flatMap((x) => extractStrings(x));
  }
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return extractStrings(parsed);
      } catch {
        // fall through
      }
    }
    return [trimmed];
  }
  return [JSON.stringify(v)];
}

function tally(rows: DemoAttendeeRow[], pick: (r: DemoAttendeeRow) => unknown, topN = 12) {
  const counts = new Map<string, number>();
  for (const demo of uniqueDemos(rows)) {
    const items = extractStrings(pick(demo));
    const seen = new Set<string>();
    for (const raw of items) {
      const key = normalise(raw);
      if (!key) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, topN);
}

function collectAll(rows: DemoAttendeeRow[], pick: (r: DemoAttendeeRow) => unknown): string[] {
  const out: string[] = [];
  for (const demo of uniqueDemos(rows)) {
    for (const raw of extractStrings(pick(demo))) {
      const t = normalise(raw);
      if (t) out.push(t);
    }
  }
  return out;
}

function normalise(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

export function CompetitorMentionsPanel(props: MentionProps) {
  const data = useMemo(() => tally(props.rows, (r) => r.competitors_mentioned), [props.rows]);
  const unit = props.viewMode === "company" ? "company" : "demo";
  return (
    <HBarChart
      title="Competitors mentioned"
      subtitle={`From transcript-tagged follow_up_context. One vote per ${unit}.`}
      data={data}
      filterPills={pillsFor(props)}
    />
  );
}

export function IntegrationMentionsPanel(props: MentionProps) {
  const data = useMemo(() => tally(props.rows, (r) => r.integration_requests), [props.rows]);
  const unit = props.viewMode === "company" ? "company" : "demo";
  return (
    <HBarChart
      title="Integration requests"
      subtitle={`Transcript-tagged. One vote per ${unit}.`}
      data={data}
      filterPills={pillsFor(props)}
    />
  );
}

// Pain points and objections are usually one-off, sentence-length entries —
// a bar chart is misleading. Render as either a list or a word cloud.
export function PainPointsPanel(props: MentionProps) {
  return <ListOrCloudPanel {...props} pick={(r) => r.pain_points} title="Pain points raised" />;
}

export function ObjectionsPanel(props: MentionProps) {
  return <ListOrCloudPanel {...props} pick={(r) => r.objections} title="Objections raised" />;
}

type View = "list" | "cloud";

function ListOrCloudPanel({
  rows, pick, title, viewMode, selectionLabel, onClearSelection, pagePills,
}: MentionProps & {
  pick: (r: DemoAttendeeRow) => unknown;
  title: string;
}) {
  void viewMode;
  const [view, setView] = useState<View>("list");

  const items = useMemo(() => collectAll(rows, pick), [rows, pick]);
  // For the list view, dedup with counts so repeated phrasings collapse.
  const list = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of items) {
      const k = t.toLowerCase();
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    // Preserve original casing from first occurrence.
    const original = new Map<string, string>();
    for (const t of items) {
      const k = t.toLowerCase();
      if (!original.has(k)) original.set(k, t);
    }
    return Array.from(counts.entries())
      .map(([k, v]) => ({ text: original.get(k) ?? k, count: v }))
      .sort((a, b) => b.count - a.count || a.text.localeCompare(b.text));
  }, [items]);

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  if (selectionLabel) {
    pills.unshift({
      kind: "selection",
      label: `Selected: ${selectionLabel}`,
      onClear: onClearSelection,
    });
  }

  return (
    <section className="card" style={{ display: "flex", flexDirection: "column" }}>
      <SectionCardHeader
        title={<span style={{ fontSize: 14 }}>{title}</span>}
        subtitle={`Transcript-tagged. ${items.length} mention${items.length === 1 ? "" : "s"} across ${list.length} unique phrase${list.length === 1 ? "" : "s"}.`}
        filterPills={pills}
        headerExtra={<ViewToggle view={view} onChange={setView} />}
      />
      <div className="card-body">
        {items.length === 0 ? (
          <div className="empty" style={{ padding: 24 }}>No mentions in cohort.</div>
        ) : view === "list" ? (
          <ul style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            maxHeight: 360,
            overflow: "auto",
          }}>
            {list.map((row) => (
              <li
                key={row.text}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  fontSize: 12,
                  padding: "5px 8px",
                  borderRadius: 4,
                  background: "var(--bg-subtle, #FAFAF7)",
                  border: "1px solid var(--border-default)",
                }}
              >
                <span style={{ color: FG1 }}>{row.text}</span>
                {row.count > 1 && (
                  <span style={{ fontSize: 11, color: MUTED, fontVariantNumeric: "tabular-nums" }}>×{row.count}</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <WordCloud
            texts={items}
            embedded
          />
        )}
      </div>
    </section>
  );
}

function ViewToggle({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  return (
    <div role="group" aria-label="View" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
      {(
        [{ key: "list", label: "List" }, { key: "cloud", label: "Word cloud" }] as const
      ).map((opt) => {
        const active = view === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            style={{
              padding: "3px 10px",
              background: active ? PRIMARY : "transparent",
              color: active ? "#fff" : FG1,
              border: "none", cursor: "pointer", fontWeight: active ? 600 : 400,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
